import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Banknote, MapPin, Calendar, ExternalLink, Building2, TrendingUp, Calculator, Info, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import HrefLangAlternates from "@/components/HrefLangAlternates";
import { tr } from "@/lib/i18n";

const COMPANY_SIZES = [
  { v: "zzp", label_i18n: { nl: "ZZP / Eenmanszaak", en: "Solo trader", de: "Einzelunternehmer", fr: "Indépendant" } },
  { v: "micro", label_i18n: { nl: "Micro (< 10)", en: "Micro (< 10)", de: "Mikro (< 10)", fr: "Micro (< 10)" } },
  { v: "small", label_i18n: { nl: "Klein (10-49)", en: "Small (10-49)", de: "Klein (10-49)", fr: "Petite (10-49)" } },
  { v: "medium", label_i18n: { nl: "Middelgroot (50-249)", en: "Medium (50-249)", de: "Mittel (50-249)", fr: "Moyenne (50-249)" } },
  { v: "large", label_i18n: { nl: "Groot (250+)", en: "Large (250+)", de: "Groß (250+)", fr: "Grande (250+)" } },
];

const T = {
  nl: { hero: "Subsidie-adviseur", title: "Bespaar duizenden euro's op je kiosk-investering",
        subtitle: "Veel Europese landen subsidiëren digitalisering tot 60% van de investering. Wij brengen je in 30 seconden in kaart welke regelingen op jouw project passen.",
        step1: "Land", step2: "Bedrijfsgrootte", step3: "Geplande investering",
        results: "Passende subsidies", noresult: "Selecteer hierboven je land om de mogelijkheden te zien.",
        funding_grant: "Subsidie", funding_tax_credit: "Belastingaftrek", funding_voucher: "Voucher", funding_low_interest_loan: "Lening",
        coverage: "Dekking", maxAmount: "Maximum", deadline: "Deadline", rolling: "Doorlopend",
        provider: "Provider", apply: "Officiële website", savings: "Geschatte besparing",
        combinable: "Combineerbaar?", combinableYes: "Verschillende regelingen zijn vaak stapelbaar. Wij helpen je bij de juiste stack.",
        cta_title: "Wij regelen de subsidie-aanvraag voor je", cta_body: "Wil je niet zelf door RVO/BAFA/Bpifrance? Vraag een gratis intake aan, ervaren consultant gaat aan de slag.",
        cta_btn: "Plan een intake",
        disclaimer: "Subsidieregelingen wijzigen regelmatig. Cijfers gecontroleerd februari 2026, voor actuele info klik door naar de officiële provider.",
  },
  en: { hero: "Grant Advisor", title: "Save thousands on your kiosk investment",
        subtitle: "Many European countries subsidise digitalisation up to 60% of investment. We map the right schemes for your project in 30 seconds.",
        step1: "Country", step2: "Company size", step3: "Planned investment",
        results: "Matching subsidies", noresult: "Select a country above to see options.",
        funding_grant: "Grant", funding_tax_credit: "Tax credit", funding_voucher: "Voucher", funding_low_interest_loan: "Loan",
        coverage: "Coverage", maxAmount: "Maximum", deadline: "Deadline", rolling: "Rolling",
        provider: "Provider", apply: "Official website", savings: "Estimated savings",
        combinable: "Stackable?", combinableYes: "Different schemes are often combinable. We help you build the right stack.",
        cta_title: "We file the application for you", cta_body: "Don't want to navigate RVO/BAFA/Bpifrance yourself? Book a free intake, experienced consultant takes over.",
        cta_btn: "Schedule intake",
        disclaimer: "Subsidy schemes change frequently. Numbers verified February 2026, click through for live info.",
  },
  de: { hero: "Förder-Beratung", title: "Sparen Sie tausende Euro bei Ihrer Kiosk-Investition",
        subtitle: "Viele europäische Länder fördern Digitalisierung bis zu 60% der Investition. Wir finden in 30 Sekunden die passenden Programme.",
        step1: "Land", step2: "Unternehmensgröße", step3: "Geplante Investition",
        results: "Passende Förderungen", noresult: "Land oben auswählen, um Optionen zu sehen.",
        funding_grant: "Zuschuss", funding_tax_credit: "Steuergutschrift", funding_voucher: "Gutschein", funding_low_interest_loan: "Darlehen",
        coverage: "Förderquote", maxAmount: "Maximum", deadline: "Deadline", rolling: "Laufend",
        provider: "Anbieter", apply: "Offizielle Website", savings: "Geschätzte Ersparnis",
        combinable: "Kombinierbar?", combinableYes: "Verschiedene Programme sind oft stapelbar. Wir bauen die richtige Kombination.",
        cta_title: "Wir beantragen die Förderung für Sie", cta_body: "Sie möchten nicht selbst durch BMWK/BAFA navigieren? Vereinbaren Sie ein kostenloses Erstgespräch.",
        cta_btn: "Termin vereinbaren",
        disclaimer: "Förderprogramme ändern sich häufig. Zahlen verifiziert Februar 2026.",
  },
  fr: { hero: "Conseil en subventions", title: "Économisez des milliers d'euros sur votre investissement kiosque",
        subtitle: "De nombreux pays européens subventionnent la numérisation jusqu'à 60% de l'investissement. Nous identifions en 30 secondes les programmes adaptés.",
        step1: "Pays", step2: "Taille d'entreprise", step3: "Investissement prévu",
        results: "Subventions correspondantes", noresult: "Sélectionnez un pays ci-dessus.",
        funding_grant: "Subvention", funding_tax_credit: "Crédit d'impôt", funding_voucher: "Chèque", funding_low_interest_loan: "Prêt",
        coverage: "Couverture", maxAmount: "Maximum", deadline: "Date limite", rolling: "Permanent",
        provider: "Organisme", apply: "Site officiel", savings: "Économies estimées",
        combinable: "Cumulable?", combinableYes: "Les dispositifs sont souvent cumulables.",
        cta_title: "Nous montons le dossier pour vous", cta_body: "Vous préférez ne pas naviguer Bpifrance/France Num? Réservez un entretien gratuit.",
        cta_btn: "Planifier un entretien",
        disclaimer: "Les dispositifs évoluent. Chiffres vérifiés février 2026.",
  },
  es: { hero: "Asesoría de subvenciones", title: "Ahorra miles de euros en tu inversión en kiosco",
        subtitle: "Muchos países europeos subvencionan la digitalización hasta el 60% de la inversión. Mapeamos los programas adecuados para tu proyecto en 30 segundos.",
        step1: "País", step2: "Tamaño de empresa", step3: "Inversión prevista",
        results: "Subvenciones aplicables", noresult: "Selecciona un país arriba para ver opciones.",
        funding_grant: "Subvención", funding_tax_credit: "Crédito fiscal", funding_voucher: "Bono", funding_low_interest_loan: "Préstamo",
        coverage: "Cobertura", maxAmount: "Máximo", deadline: "Fecha límite", rolling: "Continuo",
        provider: "Proveedor", apply: "Sitio oficial", savings: "Ahorro estimado",
        combinable: "¿Compatibles?", combinableYes: "Los distintos programas suelen ser compatibles. Te ayudamos a combinarlos correctamente.",
        cta_title: "Gestionamos la solicitud por ti", cta_body: "¿No quieres lidiar con CDTI/ICEX/ENISA tú mismo? Reserva una consulta gratuita.",
        cta_btn: "Reservar consulta",
        disclaimer: "Los programas de subvención cambian con frecuencia. Cifras verificadas en febrero de 2026.",
  },
  it: { hero: "Consulenza sovvenzioni", title: "Risparmia migliaia di euro sul tuo investimento in chioschi",
        subtitle: "Molti paesi europei sovvenzionano la digitalizzazione fino al 60% dell'investimento. Mappiamo i programmi adatti al tuo progetto in 30 secondi.",
        step1: "Paese", step2: "Dimensione aziendale", step3: "Investimento previsto",
        results: "Sovvenzioni applicabili", noresult: "Seleziona un paese sopra per vedere le opzioni.",
        funding_grant: "Sovvenzione", funding_tax_credit: "Credito d'imposta", funding_voucher: "Voucher", funding_low_interest_loan: "Prestito",
        coverage: "Copertura", maxAmount: "Massimo", deadline: "Scadenza", rolling: "Continuo",
        provider: "Ente erogatore", apply: "Sito ufficiale", savings: "Risparmio stimato",
        combinable: "Cumulabili?", combinableYes: "I diversi programmi sono spesso cumulabili. Ti aiutiamo a combinarli correttamente.",
        cta_title: "Gestiamo la richiesta per te", cta_body: "Non vuoi navigare da solo MIMIT/Invitalia? Prenota una consulenza gratuita.",
        cta_btn: "Prenota consulenza",
        disclaimer: "I programmi cambiano frequentemente. Cifre verificate a febbraio 2026.",
  },
  da: { hero: "Tilskudsrådgiver", title: "Spar tusindvis af kroner på din kiosk-investering",
        subtitle: "Mange europæiske lande støtter digitalisering med op til 60% af investeringen. Vi kortlægger de rette ordninger til dit projekt på 30 sekunder.",
        step1: "Land", step2: "Virksomhedsstørrelse", step3: "Planlagt investering",
        results: "Matchende tilskud", noresult: "Vælg et land ovenfor for at se muligheder.",
        funding_grant: "Tilskud", funding_tax_credit: "Skattefradrag", funding_voucher: "Voucher", funding_low_interest_loan: "Lån",
        coverage: "Dækning", maxAmount: "Maksimum", deadline: "Frist", rolling: "Løbende",
        provider: "Udbyder", apply: "Officiel side", savings: "Estimeret besparelse",
        combinable: "Kombinerbar?", combinableYes: "Forskellige ordninger kan ofte kombineres. Vi hjælper med den rigtige sammensætning.",
        cta_title: "Vi håndterer ansøgningen for dig", cta_body: "Vil du ikke selv navigere EIFO/Innovationsfonden? Book et gratis indledende møde.",
        cta_btn: "Book møde",
        disclaimer: "Tilskudsordninger ændres ofte. Tal verificeret februar 2026.",
  },
};

export default function SubsidyAdvisorPage() {
  const { lang } = useApp();
  const tt = T[lang] || T.nl;
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(() => localStorage.getItem("kc_subsidy_country") || "");
  const [companySize, setCompanySize] = useState(() => localStorage.getItem("kc_subsidy_size") || "small");
  const [investment, setInvestment] = useState(() => parseInt(localStorage.getItem("kc_subsidy_invest")) || 25000);
  const [subsidies, setSubsidies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/subsidies/countries").then((r) => setCountries(r.data || [])); }, []);

  useEffect(() => {
    localStorage.setItem("kc_subsidy_country", country);
    localStorage.setItem("kc_subsidy_size", companySize);
    localStorage.setItem("kc_subsidy_invest", String(investment));
    if (!country) { setSubsidies([]); return; }
    setLoading(true);
    api.get("/subsidies", { params: { country, company_size: companySize, min_investment: investment } })
      .then((r) => setSubsidies(r.data || []))
      .finally(() => setLoading(false));
  }, [country, companySize, investment]);

  const totalSavings = useMemo(() => {
    // Be conservative, pick the single largest applicable subsidy (since not all are stackable)
    return subsidies.reduce((m, s) => Math.max(m, s.expected_savings_eur || 0), 0);
  }, [subsidies]);

  const canonical = typeof window !== "undefined" ? `${window.location.origin}/subsidies` : "";

  return (
    <div className="min-h-screen pb-20" data-testid="subsidy-advisor">
      <Helmet>
        <html lang={lang} />
        <title>{`${tt.title}, Kiosk Configurator`}</title>
        <meta name="description" content={tt.subtitle} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${tt.hero}, Kiosk Configurator`} />
        <meta property="og:description" content={tt.subtitle} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "GovernmentService",
          "name": tt.hero,
          "description": tt.subtitle,
          "areaServed": { "@type": "Place", "name": "European Union" },
          "provider": { "@type": "Organization", "name": "Kiosk Configurator" },
        })}</script>
      </Helmet>
      <HrefLangAlternates path="/subsidies" />

      {/* Hero */}
      <section className="container-x pt-12 pb-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#39C46F] mb-3">
          <Banknote className="w-3.5 h-3.5" /> {tt.hero}
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-[#3D3B00] max-w-3xl leading-tight" style={{ fontFamily: '"Outfit", sans-serif' }}>
          {tt.title}
        </h1>
        <p className="text-base text-[#4F4D2A] mt-4 max-w-2xl">{tt.subtitle}</p>
      </section>

      {/* Wizard */}
      <section className="container-x">
        <div className="card-surface p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Country */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#3D3B00] font-bold flex items-center gap-1.5 mb-2">
                <MapPin className="w-3 h-3" /> {tt.step1}
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {countries.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCountry(c.code === country ? "" : c.code)}
                    data-testid={`subsidy-country-${c.code}`}
                    className={`px-2 py-3 rounded-md border text-xs font-semibold transition flex flex-col items-center justify-center gap-1 ${country === c.code ? "bg-[#39C46F] border-[#39C46F] text-white" : "bg-[#FFFFFF] border-[#E5DFC8] text-[#4F4D2A] hover:border-[#39C46F]"}`}
                  >
                    <span className="text-base">{getFlag(c.code)}</span>
                    <span className="text-[10px]">{c.names?.[lang] || c.names?.en || c.code}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Company size */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#3D3B00] font-bold flex items-center gap-1.5 mb-2">
                <Building2 className="w-3 h-3" /> {tt.step2}
              </label>
              <div className="space-y-1.5">
                {COMPANY_SIZES.map((s) => (
                  <button
                    key={s.v}
                    onClick={() => setCompanySize(s.v)}
                    data-testid={`subsidy-size-${s.v}`}
                    className={`w-full text-left px-3 py-2 rounded-md border text-xs font-medium transition ${companySize === s.v ? "bg-[#39C46F] border-[#39C46F] text-white" : "bg-[#FFFFFF] border-[#E5DFC8] text-[#4F4D2A] hover:border-[#39C46F]"}`}
                  >
                    {tr(s.label_i18n, lang) || s.label_i18n.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Investment */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#A8B3AD] flex items-center gap-1.5 mb-2">
                <Calculator className="w-3 h-3" /> {tt.step3}
              </label>
              <div className="bg-white border border-[#E5DFC8] rounded-md p-4">
                <div className="text-2xl font-semibold text-[#3D3B00] mb-3">
                  €{investment.toLocaleString(lang === "nl" ? "nl-NL" : "en-US")}
                </div>
                <input
                  type="range"
                  min={5000}
                  max={250000}
                  step={1000}
                  value={investment}
                  onChange={(e) => setInvestment(parseInt(e.target.value))}
                  data-testid="subsidy-invest-slider"
                  className="w-full accent-[#39C46F]"
                />
                <div className="flex justify-between text-[10px] text-[#7A7860] mt-1">
                  <span>€5K</span><span>€50K</span><span>€100K</span><span>€250K</span>
                </div>
              </div>
            </div>
          </div>

          {/* Total banner */}
          {country && totalSavings > 0 && (
            <div className="mt-5 p-4 rounded-lg flex items-center justify-between flex-wrap gap-3" style={{ background: '#102E22', border: '1px solid rgba(57,196,111,0.35)' }}>
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-[#9AE5C6]" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#9AE5C6]">{tt.savings}</div>
                  <div className="text-2xl font-semibold text-white">€{Math.round(totalSavings).toLocaleString(lang === "nl" ? "nl-NL" : "en-US")}</div>
                </div>
              </div>
              <div className="text-xs text-[#F4F1EA] max-w-md">{tt.combinableYes}</div>
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="container-x mt-8">
        <h2 className="text-xl font-semibold text-[#3D3B00] mb-4 flex items-center gap-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
          <TrendingUp className="w-5 h-5 text-[#39C46F]" /> {tt.results}
          {country && <span className="text-sm font-normal text-[#7A7860]">— {subsidies.length}</span>}
        </h2>
        {!country && (
          <div className="card-surface p-10 text-center text-[#A8B3AD]" data-testid="subsidy-empty">
            <Banknote className="w-10 h-10 mx-auto mb-3 opacity-30" />
            {tt.noresult}
          </div>
        )}
        {country && loading && <div className="text-center text-[#A8B3AD] py-10">…</div>}
        {country && !loading && subsidies.length === 0 && (
          <div className="card-surface p-10 text-center text-[#7A7860]">{tt.noresult || "No matching schemes found for this combination."}</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subsidies.map((s) => <SubsidyCard key={s.id} subsidy={s} lang={lang} tt={tt} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x mt-12">
        <div className="card-surface p-6 sm:p-8 bg-gradient-to-br from-[#D97742]/10 to-transparent border-[#D97742]/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <h3 className="text-xl font-semibold text-white">{tt.cta_title}</h3>
            <p className="text-sm text-[#A8B3AD] mt-1 max-w-xl">{tt.cta_body}</p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#D97742] text-white font-semibold hover:bg-[#c46939]" data-testid="subsidy-cta">
            {tt.cta_btn} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="container-x mt-6">
        <p className="text-[11px] text-[#5d6a64] flex items-start gap-1.5">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          {tt.disclaimer}
        </p>
      </section>
    </div>
  );
}

function SubsidyCard({ subsidy, lang, tt }) {
  const name = tr(subsidy.name_i18n, lang) || subsidy.name_i18n?.en || subsidy.name_i18n?.nl || subsidy.slug;
  const short = tr(subsidy.short_i18n, lang) || subsidy.short_i18n?.en || subsidy.short_i18n?.nl;
  const desc = tr(subsidy.description_i18n, lang) || subsidy.description_i18n?.en || subsidy.description_i18n?.nl;
  const fundingLabel = {
    grant: tt.funding_grant, tax_credit: tt.funding_tax_credit, voucher: tt.funding_voucher, low_interest_loan: tt.funding_low_interest_loan,
  }[subsidy.funding_type] || subsidy.funding_type;

  return (
    <article className="card-surface p-5 flex flex-col hover:translate-y-[-2px] transition" data-testid={`subsidy-card-${subsidy.slug}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg">{getFlag(subsidy.country_code)}</span>
          {subsidy.region && <span className="text-[10px] text-[#A8B3AD]">{subsidy.region}</span>}
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1F3A2E] text-[#7FD8B8]">{fundingLabel}</span>
        </div>
        {subsidy.expected_savings_eur > 0 && (
          <div className="text-right shrink-0">
            <div className="text-[9px] uppercase tracking-wider text-[#7FD8B8]">{tt.savings}</div>
            <div className="text-base font-semibold text-[#7FD8B8]">€{Math.round(subsidy.expected_savings_eur).toLocaleString(lang === "nl" ? "nl-NL" : "en-US")}</div>
          </div>
        )}
      </div>
      <h3 className="text-base font-semibold text-white">{name}</h3>
      {short && <p className="text-xs text-[#D97742] mt-1.5 font-medium">{short}</p>}
      {desc && <p className="text-xs text-[#A8B3AD] mt-2 line-clamp-3 flex-1">{desc}</p>}

      <div className="mt-3 pt-3 border-t border-[#1F3A2E] grid grid-cols-3 gap-2 text-[10px]">
        <Stat label={tt.coverage} value={subsidy.coverage_pct ? `${subsidy.coverage_pct}%` : "—"} />
        <Stat label={tt.maxAmount} value={subsidy.max_amount_eur ? `€${Math.round(subsidy.max_amount_eur / 1000)}K` : "—"} />
        <Stat label={tt.deadline} value={subsidy.deadline === "rolling" ? tt.rolling : (subsidy.deadline || "—")} />
      </div>

      {subsidy.official_url && (
        <a href={subsidy.official_url} target="_blank" rel="noopener noreferrer"
           data-testid={`subsidy-link-${subsidy.slug}`}
           className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#D97742] hover:text-[#c46939]">
          {tt.apply} <ExternalLink className="w-3 h-3" />
          {subsidy.provider_name && <span className="text-[#A8B3AD] ml-1">· {subsidy.provider_name}</span>}
        </a>
      )}
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#A8B3AD]">{label}</div>
      <div className="text-xs font-semibold text-white">{value}</div>
    </div>
  );
}

function getFlag(code) {
  const m = { NL: "🇳🇱", BE: "🇧🇪", DE: "🇩🇪", FR: "🇫🇷", AT: "🇦🇹", EU: "🇪🇺", IT: "🇮🇹", ES: "🇪🇸", DK: "🇩🇰", SE: "🇸🇪" };
  return m[code] || "🌍";
}
